import { DatePipe } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { Meta } from '@angular/platform-browser';
import { LucideAngularModule, icons } from 'lucide-angular';
import { Subscription, take } from 'rxjs';
import { ApiFactoryEndPoints } from './common/api-factory-end-points';
import { AppConfigService } from './services/app-config.service';
import { ContactService } from './services/contact.service';
import { LocalStorageService } from './services/local-storage.service';
import { WebUserInfo } from './data/web-user-info';
import { LoaderComponent } from './common/loader/loader.component';
import { CookieConsentComponent } from './common/cookie-consent/cookie-consent.component';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../environments/environment';
import { MessagingService } from './services/messaging.service';
import { FCMMessageRegistration } from './Model/fcmmessage-registration';
import { UserService } from './services/user.service';
import { ToastService } from './services/toast.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { initializeApp } from "firebase/app";

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, LucideAngularModule, LoaderComponent, CookieConsentComponent],
  template: `
    <!-- Global loader visible across all routes -->
    <app-loader></app-loader>

        <!-- Global cookie consent banner -->
    <app-cookie-consent></app-cookie-consent>

    <!-- Page content routed here -->
    <router-outlet></router-outlet>
  `,
  styles: []
})
export class App {
  static lucideIcons = icons;
  title = 'Capital Food';
  currentPath: string = '';
  // showCta = true;

  route: string;
  locality: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  webUserInfo: WebUserInfo = new WebUserInfo();
  visitors: any;
  currentUrl: string;

  message: any = null;

  fCMMessageRegistration: FCMMessageRegistration;
  channel: BroadcastChannel;

  private saveWebUserInfoSubscription: Subscription;
  private getVisitorCountSubscription: Subscription;
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private localStorage: LocalStorageService,
    private activatedRoute: ActivatedRoute,
    private contactService: ContactService,
    private apiUrl: ApiFactoryEndPoints,
    private appConfig: AppConfigService,
    private dialog: MatDialog,
    private fcm: MessagingService,
    private userService: UserService,
    private toastService: ToastService,
    private metaService: Meta
  ) {
    // SEO: Apply noindex meta tag for private routes based on route data
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        let route = this.activatedRoute.snapshot;
        while (route.firstChild) {
          route = route.firstChild;
        }
        const noindex = route.data?.['noindex'];
        if (noindex) {
          this.metaService.updateTag({ name: 'robots', content: 'noindex, nofollow' });
        } else {
          this.metaService.updateTag({ name: 'robots', content: 'index, follow' });
        }
      }
    });
  }

  ngOnInit() {
    this.currentUrl = window.location.host.replace(':', '');

    // 🔥 Initialize Firebase first
    const firebaseConfig = environment.firebase;
    if (firebaseConfig) {
      try {
        initializeApp(firebaseConfig);
        console.log('✅ Firebase initialized');
      } catch (error) {
        console.error('❌ Firebase initialization failed:', error);
      }
    } else {
      console.error('⚠️ Firebase config not loaded!');
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(this.setGeoLocation.bind(this), this.handleLocationError.bind(this));
    }

    if (!this.channel) {
      this.channel = new window.BroadcastChannel('bg-notification');
      this.channel.addEventListener('message', (e) => {
        console.log('Received Message Title : ' + e.data.payload.notification?.title);
        console.log('Received Message Body : ' + e.data.payload.notification?.body);
        //Ensure payload structure is correct
        const payload = e.data.payload;
        if (!payload || !payload.notification) {
          console.error('Invalid notification payload:', e.data);
          return;
        }
      });
    }

    this.initFcmToken();
    this.listen();
    this.getVisitorCount();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    
    if (this.saveWebUserInfoSubscription) {
      this.saveWebUserInfoSubscription.unsubscribe();
    }
    if (this.getVisitorCountSubscription) {
      this.getVisitorCountSubscription.unsubscribe();
    }
  }

  setGeoLocation(position: { coords: { latitude: any; longitude: any } }) {
    console.log
    const { coords: { latitude, longitude }, } = position;
    this.localStorage.setLocation(latitude, longitude);
    this.getGoogleMap(latitude, longitude);
  }

  // getGoogleMap(latitude: number, longitude: number) {
  //   this.mapsAPILoader.load().then(() => {
  //     this.getAddress(latitude, longitude);
  //   });
  // }
  getGoogleMap(latitude: number, longitude: number) {
    this.loadGoogleMapsAPI().then(() => {
      this.getAddress(latitude, longitude);
    }).catch((err) => {
    });
  }

  loadGoogleMapsAPI(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if ((window as any).google && (window as any).google.maps) {
        resolve();
        return;
      }

      // Create a new <script> element with loading=async
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        resolve();
      };

      script.onerror = (error) => {
        console.error('❌ Error loading Google Maps:', error);
        reject(error);
      };

      document.head.appendChild(script);
    });
  }

  handleLocationError(error: GeolocationPositionError) {
    this.getClientInfo();
  }

  getAddress(latitude: number, longitude: number) {
    const geocoder = new google.maps.Geocoder();
    const latlng = { lat: latitude, lng: longitude };

    console.log(geocoder)
    geocoder.geocode({ location: latlng }, (results, status) => {
      if (status === "OK" && results[0]) {
        const address = results[0].address_components;
        this.extractAddressComponents(address);
      } else {
        console.error("Geocoder failed due to:", status);
      }
    });
  }

  extractAddressComponents(address: google.maps.GeocoderAddressComponent[]) {
    for (const component of address) {
      switch (true) {
        case component.types.includes('route'):
          this.route = component.long_name;
          break;
        case component.types.includes('sublocality_level_1'):
          this.locality = component.long_name;
          break;
        case component.types.includes('locality'):
          this.city = component.long_name;
          break;
        case component.types.includes('administrative_area_level_1'):
          this.state = component.long_name;
          break;
        case component.types.includes('country'):
          this.country = component.long_name;
          break;
        case component.types.includes('postal_code'):
          this.postalCode = component.long_name;
          break;
        default:
          break;
      }
    }
    this.getClientInfo();
  }

  getClientInfo() {
    this.getClientIPAddress();
    let currentDateTime = this.transform((new Date).toUTCString());
    if (!this.getCookie("inTime")) {
      this.setCookie("inTime", currentDateTime, 1);
    }
    this.webUserInfo.intiatedFrom = this.getIntiatedFrom();
    this.webUserInfo.clientIp = JSON.parse(this.localStorage.getItem("client_ip"));
    this.webUserInfo.cleintBrowser = this.detectBrowserName();
    this.webUserInfo.cleintDevice = this.isMobileTabletOrPC();
    this.webUserInfo.cleintBrowserVersion = this.getBrowserVersion();
    this.webUserInfo.sessionId = this.getJSessionId();
    this.webUserInfo.inTime = this.getCookie("inTime");
    this.webUserInfo.outTime = currentDateTime;
    this.webUserInfo.route = this.route;
    this.webUserInfo.locality = this.locality;
    this.webUserInfo.city = this.city;
    this.webUserInfo.state = this.state;
    this.webUserInfo.country = this.country;
    this.webUserInfo.postalCode = this.postalCode;
    this.webUserInfo.latitude = this.localStorage.getLocation().lat.toString();
    this.webUserInfo.longitude = this.localStorage.getLocation().lon.toString();
    this.webUserInfo.sourceUrl = this.currentUrl;

    this.saveWebUserInfo();
  }

  transform(value: string) {
    var datePipe = new DatePipe("en-US");
    value = datePipe.transform(value, 'MM/dd/yyyy h:mm:ss');
    return value;
  }

  getClientIPAddress() {
    let clientIp = JSON.parse(this.localStorage.getItem("client_ip"));
    if (!clientIp) {
      var xhttp = new XMLHttpRequest();
      xhttp.onreadystatechange = () => {
        if (xhttp.readyState == 4 && xhttp.status == 200) {
          const ip = xhttp.responseText;
          this.localStorage.setItem("client_ip", JSON.stringify(ip));
        }
      };
      xhttp.open("GET", "https://api.ipify.org", true); // Ensure the URL starts with https://
      xhttp.send();
    }
  }

  private getCookie(name: string) {
    let ca: Array<string> = document.cookie.split(';');
    let caLen: number = ca.length;
    let cookieName = `${name}=`;
    let c: string;

    for (let i: number = 0; i < caLen; i += 1) {
      c = ca[i].replace(/^\s+/g, '');
      if (c.indexOf(cookieName) == 0) {
        return c.substring(cookieName.length, c.length);
      }
    }
    return '';
  }

  private setCookie(name: string, value: string, expireDays: number, path: string = '') {
    let d: Date = new Date();
    d.setTime(d.getTime() + expireDays * 24 * 60 * 60 * 1000);
    let expires: string = `expires=${d.toUTCString()}`;
    let cpath: string = path ? `; path=${path}` : '';
    document.cookie = `${name}=${value}; ${expires}${cpath}`;
  }

  getIntiatedFrom(): string {
    let referrer = document.referrer;

    this.activatedRoute.queryParams.pipe(take(1)).subscribe(params => {
      if ('referrer' in params) {
        referrer = params['referrer'];
      }
    });

    return referrer;
  }

  detectBrowserName() {
    const agent = window.navigator.userAgent.toLowerCase()
    switch (true) {
      case agent.indexOf('edge') > -1:
        return 'edge';
      case agent.indexOf('opr') > -1 && !!(<any>window).opr:
        return 'opera';
      case agent.indexOf('chrome') > -1 && !!(<any>window).chrome:
        return 'chrome';
      case agent.indexOf('trident') > -1:
        return 'ie';
      case agent.indexOf('firefox') > -1:
        return 'firefox';
      case agent.indexOf('safari') > -1:
        return 'safari';
      default:
        return 'other';
    }
  }

  isMobileTabletOrPC(): string | undefined {
    const uA = window.navigator.userAgent || window.navigator.vendor;
    if ((/iPad|iPhone|iPod/.test(uA)) || (uA.includes('Mac') && 'ontouchend' in document)) {
      return 'iOS';
    }
    const osList = ['Windows', 'Android', 'Unix', 'Mac', 'Linux', 'BlackBerry'];
    for (const os of osList) {
      if (new RegExp(os, 'i').test(uA)) return os;
    }
    return undefined;
  }

  getBrowserVersion() {
    var browserVersion = '' + parseFloat(navigator.appVersion);
    var Offset, OffsetVersion, ix;
    var browserAgent = navigator.userAgent;

    if ((OffsetVersion = browserAgent.indexOf("Chrome")) != -1) {
      browserVersion = browserAgent.substring(OffsetVersion + 7);
    }
    // For Microsoft internet explorer 
    else if ((OffsetVersion = browserAgent.indexOf("MSIE")) != -1) {
      browserVersion = browserAgent.substring(OffsetVersion + 5);
    }
    // For Safari
    else if ((OffsetVersion = browserAgent.indexOf("Safari")) != -1) {
      browserVersion = browserAgent.substring(OffsetVersion + 7);
      if ((OffsetVersion = browserAgent.indexOf("Version")) != -1)
        browserVersion = browserAgent.substring(OffsetVersion + 8);
    }
    return browserVersion;
  }

  getJSessionId() {
    return /SESS\w*ID=([^;]+)/i.test(document.cookie) ? RegExp.$1 : "false";
  }

  saveWebUserInfo() {
    console.log("saveuserinfo called")
    if (this.webUserInfo.clientIp != '182.79.71.219' && this.webUserInfo.clientIp != '182.79.71.218') {
      this.saveWebUserInfoSubscription = this.contactService.saveWebUserInfo(this.apiUrl.insertWebUserInfo, this.webUserInfo).subscribe((data) => {
        if (data) {
          this.localStorage.setItem("web_user_info", JSON.stringify(this.webUserInfo));
        }
      });
    }
  }

  private async initFcmToken() {
    try {
      const token = await this.fcm.requestPermissionAndGetToken();
      // console.log(token)
      if (token) {
        this.handleToken(token);
      }
    } catch (e) {
      console.error('FCM init failed', e);
    }
  }

  handleToken(token: string) {
    if (!token) return;

    // 🔹 Get values ONCE
    const registeredToken = localStorage.getItem('FirebaseTokenRegistered');
    const deviceId = localStorage.getItem('DeviceID');
    const emailRaw = localStorage.getItem('google_email');

    // 🔹 Always store latest token
    localStorage.setItem('FirebaseToken', token);

    // 🔹 Must be logged in
    if (!emailRaw || emailRaw === 'NA') {
      console.log('⏳ User not logged in — skipping FCM registration');
      return;
    }

    // 🔹 Skip duplicate registration
    if (registeredToken === token) {
      console.log('✅ Token already registered — skipping API');
      return;
    }

    const email = JSON.parse(emailRaw);

    console.log('🔄 Registering FCM token to server');

    this.fCMMessageRegistration = new FCMMessageRegistration();
    this.fCMMessageRegistration.deviceId = deviceId;
    this.fCMMessageRegistration.locId = 0;
    this.fCMMessageRegistration.locCusId = email;
    this.fCMMessageRegistration.token = token;
    this.fCMMessageRegistration.deviceType = "Cus";
    this.fCMMessageRegistration.applicationName = "CUI";

    this.saveFCMTokenData(token);
  }

  saveFCMTokenData(currentToken: string) {

    const deviceId = this.fCMMessageRegistration?.deviceId;
    const token = this.fCMMessageRegistration?.token;

    if (!deviceId || deviceId === 'NA') {
      console.log('❌ Device ID invalid');
      return;
    }

    if (!token) {
      console.log('❌ Token missing');
      return;
    }

    this.userService.saveFCMRegistrationData(this.apiUrl.getUserSaveTokenIdLocId, this.fCMMessageRegistration).subscribe({
      next: () => {
        console.log('✅ FCM Token Saved For Web in DB');
        localStorage.setItem('FirebaseTokenRegistered', currentToken);
      },
      error: (err) => {
        console.error('❌ FCM registration failed', err);
      }
    });
  }

  listen() {
    import('firebase/messaging')
      .then(({ getMessaging, onMessage }) => {
        const messaging = getMessaging();
        onMessage(messaging, (payload) => {
          console.log('Message received. ', payload);
          this.message = payload;
          this.toastService.showSuccessToast(this.message.notification.body);
        });
      })
      .catch(err => {
        console.error('Failed to load Firebase messaging listener:', err);
      });
  }

  getVisitorCount() {
    let clientIp = JSON.parse(this.localStorage.getItem("client_ip"));
    const api = clientIp != '182.79.71.219' && '182.79.71.218'
      ? this.apiUrl.getVisitorCount   // increments count
      : this.apiUrl.getCount;         // just fetch count
    this.getVisitorCountSubscription = this.contactService.getVisitorCount(api, this.currentUrl, "home").subscribe((data: any) => {
      this.visitors = data;
      this.localStorage.setItem('visitors', JSON.stringify(this.visitors));
    });
  }

}